import mainStyle from './mainStyle.module.scss';

import { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { Box, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';

import BinIcon from 'assets/images/icons/icn_table_cell_Bin.svg';
import { ReactComponent as BtnUpIcon } from 'assets/images/icons/icn_button_up_light.svg';
import { ReactComponent as BtnDownIcon } from 'assets/images/icons/icn_button_down_light.svg';
import { ReactComponent as AddIcon } from 'assets/images/icons/icn_btn_add.svg';

// Hooks
import useDataSourceManager from 'hooks/useDataSourceManager';
import useAuthScope from 'hooks/useAuthScope';

// Const
import { captivePortalConfig } from './const';

// Components
import DrawerField from 'components/Drawer';
import SearchInput from 'components/SearchInput';
import PaginationField from 'components/Pagination';
import GlobalAlert from 'components/Notification';
import { SolidButton } from 'components/buttons/SolidButton';
import { SolidButton as SolidRectangleButton } from 'components/buttons/SolidRectangleButton';
import Loading from './components/Loading';

import PreviewModal from './Modal/previewModal';
import CaptivePortalTable from './captivePortalTable';
import RemoveProfileModal from './Modal/removeProfileModal';
import RemoveCaptivePortalModal from './Modal/removeCaptivePortalModal';
import AddCaptivePortalModal from './Modal/addCaptivePortalModal';

// const checkEnvironment = process.env.REACT_APP_RUNNING_CONTEXT;
const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const StyledDropdown = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: theme.spacing(1),
  padding: '10px 20px',
  width: '251px',
  backgroundColor: theme.palette.primary.lightBlue,
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
  borderRadius: 4,
  zIndex: 10,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-8px',
    left: '14px',
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderBottom: `8px solid ${theme.palette.primary.lightBlue}`,
  },
}));

const CaptivePortal = ({ showEditGeneral }) => {
  const { auth, isAuthReady } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [captivePortalUuid, _setCaptivePortalUuid] = useState(uuidv4());
  const [deleteCaptivePortalUuid, _setDeleteCaptivePortalUuid] = useState(uuidv4());
  const [getWhitelistUuid, _setGetWhitelistUuid] = useState(uuidv4());
  const [isGetCaptivePortalReady, setIsGetCaptivePortalReady] = useState(false);

  const [openAction, setOpenAction] = useState(false);
  const [searchInput, setSearchInput] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [openRemoveProfileModal, setOpenRemoveProfileModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);

  const [hasChecked, setHasChecked] = useState(false);
  const [isSelectedCount, setIsSelectedCount] = useState(0);
  const [selectedRowItem, setSelectedRowItem] = useState(null);

  const [originalTableData, setOriginalTableData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [formData, setFormData] = useState(cloneDeep(captivePortalConfig));
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isLoading, setIsLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const closeRemoveModal = () => {
    setOpenRemoveModal(false);
    setOpenAction(false);
    setIsSelectedCount(0);
    setTableData(prev => prev.map(item => ({ ...item, checked: false })));
  };

  const clickRemoveBtn = () => {
    setIsLoading(true);

    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/settings/captivePortals.delete`,
      site: `${baseUrl}/sites/:siteId/settings/captivePortals.delete`,
    };

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
    };

    const apiKey = apiConfig[auth.scopeLevel];
    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    const removeIdList = tableData.filter(item => item.checked).map(item => item.id);

    const payload = {
      captivePortalIds: removeIdList,
    };

    sendRequest(apiKey, deleteCaptivePortalUuid, restfulParams, payload);

    setOpenRemoveModal(false);
    setOpenAction(false);
  };

  const clickRemoveProfileBtn = isRemoveProfileId => {
    if (isRemoveProfileId) {
      setIsLoading(true);

      const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
      const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

      const apiConfig = {
        org: `${baseUrl}/settings/captivePortals.delete`,
        site: `${baseUrl}/sites/:siteId/settings/captivePortals.delete`,
      };

      const restfulParamsConfig = {
        standalone: {
          orgId: auth.realm?.id,
          siteId: auth.sites?.id,
        },
        nonStandalone: {
          entId: auth.realm?.id,
          orgId: auth.realm?.id,
          siteId: auth.sites?.id,
        },
      };

      const apiKey = apiConfig[auth.scopeLevel];
      const mode = standalone ? 'standalone' : 'nonStandalone';
      const restfulParams = restfulParamsConfig[mode];

      const payload = {
        captivePortalIds: [],
      };
      payload.captivePortalIds.push(isRemoveProfileId);

      sendRequest(apiKey, deleteCaptivePortalUuid, restfulParams, payload);
    }

    setDrawerOpen(false);
    setOpenRemoveProfileModal(false);
    setSelectedRowItem(null);
  };

  const changeSearchInput = newValue => {
    setSearchInput(newValue);

    const tmpList = cloneDeep(originalTableData);
    if (tmpList?.length > 0) {
      const result = tmpList.filter(item => item.captivePortalName.toLowerCase().includes(newValue.toLowerCase()));
      setCurrentPage(0);
      setTableData(result);
    }
  };

  const parseCaptivePortalList = allData => {
    const finalList = [];
    if (allData?.length > 0) {
      allData.forEach(item => {
        finalList.push({
          config: item,
          id: item.id,
          checked: false,
          captivePortalName: item.name,
          authTypeLabel: item.authType === 'CLICK_THROUGH' ? 'Click-though' : allData.authType,
        });
      });
    }
    return finalList;
  };

  const reloadPage = data => {
    const isTableData = parseCaptivePortalList(data);
    setTableData(isTableData);
    setOriginalTableData(isTableData);
  };

  const initCaptivePortal = () => {
    setCurrentPage(0);
    setIsLoading(true);

    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/settings/captivePortals.read`,
      site: `${baseUrl}/sites/:siteId/settings/captivePortals.read`,
    };

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
    };

    const apiKey = apiConfig[auth.scopeLevel];
    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    sendRequest(apiKey, captivePortalUuid, restfulParams);
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [rowsPerPage]);

  useEffect(() => {
    if (!isAuthReady) return;
    initCaptivePortal();
  }, [isAuthReady]);

  useEffect(() => {
    if (!isGetCaptivePortalReady) return;

    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/settings/profileMacAddrs.read`,
      site: `${baseUrl}/sites/:siteId/settings/profileMacAddrs.read`,
    };

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
    };

    const apiKey = apiConfig[auth.scopeLevel];
    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    sendRequest(apiKey, getWhitelistUuid, restfulParams);
  }, [isGetCaptivePortalReady]);

  useEffect(() => {
    const isGetResponse = getData(captivePortalUuid);
    if (isGetResponse) {
      if (checkMockup) {
        if (isGetResponse && isGetResponse?.data) {
          setIsLoading(false);
          setIsGetCaptivePortalReady(true);
          reloadPage(isGetResponse.data);
        }
      } else {
        if (isGetResponse.hasOwnProperty('code') && isGetResponse?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: isGetResponse?.message,
          });
        } else {
          setIsLoading(false);
          setIsGetCaptivePortalReady(true);
          reloadPage(isGetResponse.data);
        }
      }
    } else {
      setIsLoading(false);
    }

    const isDeleteResponse = getData(deleteCaptivePortalUuid);
    if (isDeleteResponse) {
      if (checkMockup) {
        if (isDeleteResponse && isDeleteResponse?.data) {
          setIsLoading(false);
          reloadPage(isDeleteResponse.data);
        }
      } else {
        if (isDeleteResponse.hasOwnProperty('code') && isDeleteResponse?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: isDeleteResponse?.message,
          });
        } else {
          setIsLoading(false);
          initCaptivePortal();
        }
      }
    } else {
      setIsLoading(false);
    }

    const isGetWhitelistResponse = getData(getWhitelistUuid);
    if (isGetWhitelistResponse) {
      if (checkMockup) {
        if (isGetWhitelistResponse && isGetWhitelistResponse?.data) {
          const originWhitelist = isGetWhitelistResponse?.data;
          const tmpFormData = cloneDeep(formData);
          if (originWhitelist?.length > 0) {
            originWhitelist.forEach(item => {
              tmpFormData.whitelistList.push({
                label: item.name,
                value: item.id,
                isActive: false,
              });
            });
          }
          setFormData(tmpFormData);
        }
      } else {
        if (isGetWhitelistResponse.hasOwnProperty('code') && isGetWhitelistResponse?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: isGetWhitelistResponse?.message,
          });
        } else {
          const originWhitelist = isGetWhitelistResponse?.data;
          const tmpFormData = cloneDeep(formData);
          if (originWhitelist?.length > 0) {
            originWhitelist.forEach(item => {
              tmpFormData.whitelistList.push({
                label: item.name,
                value: item.id,
                isActive: false,
              });
            });
          }
          setFormData(tmpFormData);
        }
      }
    } else {
      setIsLoading(false);
    }
  }, [dataMap]);

  return (
    <>
      {isLoading && <Loading />}

      <Box margin="20px">
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            {hasChecked && (
              <>
                <SolidRectangleButton startIcon={openAction ? <BtnUpIcon /> : <BtnDownIcon />} width="100px" height="32px" onClick={() => setOpenAction(!openAction)}>
                  Action
                </SolidRectangleButton>
                {openAction && (
                  <StyledDropdown>
                    <Box className={`${mainStyle['action_item']}`} onClick={() => setOpenRemoveModal(true)}>
                      <img src={BinIcon} alt="icn_table_cell_Bin" />
                      <span>Remove</span>
                    </Box>
                  </StyledDropdown>
                )}
                <Divider sx={{ width: '1px', height: '40px', marginLeft: '18px' }} orientation="vertical" flexItem />
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex' }}>
            <Divider sx={{ width: '1px', height: '40px', marginRight: '18px' }} orientation="vertical" flexItem />
            <Box sx={{ marginRight: '18px' }}>
              <SearchInput placeholder="Search by Captive Portal Name" value={searchInput} onChange={newValue => changeSearchInput(newValue)} />
            </Box>
            {showEditGeneral && (
              <SolidButton sx={{ minWidth: 185, height: 32 }} startIcon={<AddIcon />} onClick={() => setOpenCreateModal(true)}>
                Create Captive Portal
              </SolidButton>
            )}
          </Box>
        </Box>

        <CaptivePortalTable
          {...{
            tableData,
            setTableData,
            hasChecked,
            setHasChecked,
            setIsSelectedCount,
            selectedRowItem,
            setSelectedRowItem,
            setDrawerOpen,
            currentPage,
            rowsPerPage,
            setOpenPreviewModal,
            showEditGeneral,
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
          <PaginationField
            name="captivePortalTable"
            pageCount={tableData?.length > 0 ? Math.max(1, Math.ceil((tableData?.length ?? 0) / rowsPerPage)) : 0}
            pageIndex={currentPage}
            pageSize={rowsPerPage}
            onPageChange={page => setCurrentPage(page)}
            onPageSizeChange={size => setRowsPerPage(size)}
          />
        </Box>
      </Box>

      {drawerOpen && (
        <DrawerField
          width="580px"
          open={drawerOpen}
          handleClose={() => {
            setDrawerOpen(false);
            setSelectedRowItem(null);
          }}
          contentKey={`Settings_CaptivePortal_Drawer_${uuidv4()}`}
          commonContent="settingsCaptivePortal"
          contentProps={{
            drawerOpen,
            setDrawerOpen,
            setSelectedRowItem,
            setOpenRemoveProfileModal,
            tableData,
            originalRowData: selectedRowItem,
            reloadPage,
            initCaptivePortal,
            formData,
            setFormData,
            showEditGeneral,
          }}
        />
      )}

      <AddCaptivePortalModal
        openCreateModal={openCreateModal}
        setOpenCreateModal={setOpenCreateModal}
        tableData={tableData}
        formData={formData}
        setFormData={setFormData}
        reloadPage={reloadPage}
        initCaptivePortal={initCaptivePortal}
      />

      <RemoveCaptivePortalModal
        openRemoveModal={openRemoveModal}
        closeRemoveModal={closeRemoveModal}
        clickRemoveBtn={clickRemoveBtn}
        isSelectedCount={isSelectedCount}
        showEditGeneral={showEditGeneral}
      />

      <RemoveProfileModal
        openRemoveProfileModal={openRemoveProfileModal}
        setOpenRemoveProfileModal={setOpenRemoveProfileModal}
        clickRemoveProfileBtn={clickRemoveProfileBtn}
        isSelectedItem={selectedRowItem}
      />

      <PreviewModal openPreviewModal={openPreviewModal} setOpenPreviewModal={setOpenPreviewModal} />

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />
    </>
  );
};

export default CaptivePortal;
